/**
 * usePrimumAssets Hook
 *
 * Provides a clean interface for accessing Primum assets hosted on CloudFront.
 * Focused on .riv animations and other media assets.
 */

import { useMemo } from 'react';

import { env } from '@/util/env';

/**
 * Hook for accessing Primum CloudFront assets
 */
export function usePrimumAssets() {
  const cloudfrontDomain = env.EXPO_PUBLIC_CLOUDFRONT_DOMAIN;

  const assets = useMemo(
    () => ({
      /**
       * Get CloudFront URL for an asset
       * @param path - The asset path relative to the CDN domain (e.g., 'animations/loading.riv')
       * @returns The full CloudFront URL
       */
      getCloudFrontUrl: (path: string): string => {
        if (!cloudfrontDomain) {
          throw new Error('CloudFront domain not configured');
        }

        return `https://${cloudfrontDomain}/${path}`;
      },

      /**
       * Check if CloudFront is available
       * @returns boolean indicating if CloudFront is configured
       */
      isCloudFrontAvailable: !!cloudfrontDomain,
    }),
    [cloudfrontDomain]
  );

  return assets;
}

/**
 * Convenience hook for .riv animation assets
 * @param path - The animation path (e.g., 'animations/loading.riv')
 * @returns The CloudFront URL for the animation
 */
export function usePrimumAnimation(path: string) {
  const { getCloudFrontUrl } = usePrimumAssets();

  return useMemo(() => getCloudFrontUrl(path), [path, getCloudFrontUrl]);
}
